<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import JournalMoodCard from '@/vue/molecules/today/journal-mood-card.vue';
import type {StackEntry} from '@/ts/types/component/today-page';

const props = defineProps<{
  entries: StackEntry[];
}>();

/**
 * Fanned stack of the user's three most recent entries. The front card flicks
 * away on click and the rest promote forward, the fan spreads on hover, and a
 * freshly saved entry flies in at the front.
 *
 * Slot geometry, easings and durations are ported verbatim from the design —
 * changing a number here changes the choreography, so treat them as fixed.
 */
interface Slot {
  x: number;
  y: number;
  rot: number;
  w: number;
  h: number;
  scale: number;
  opacity: number;
}

/** Card box, shared by every slot so only `transform` ever animates. */
const CARD_W = 285;
const CARD_H = 358;
const ORIGIN_X = 0.5;
const ORIGIN_Y = 0.6;

/**
 * Slot geometry exactly as the design authors it — some slots use a smaller
 * card box. Animating `width`/`height` is layout-bound though: every frame of a
 * 720ms promote would re-lay-out and re-raster the card's whole subtree,
 * including re-breaking the clamped note text. So the authored boxes are folded
 * into `scale` below, which the compositor can animate on its own.
 */
const AUTHORED: Slot[] = [
  {x: 67, y: 70, rot: -1, w: 285, h: 358, scale: 1, opacity: 1},
  {x: 47, y: 30, rot: 3, w: 252, h: 316, scale: 1, opacity: 1},
  {x: 17, y: 50, rot: -6, w: 252, h: 316, scale: 0.96, opacity: 1},
  {x: 27, y: 60, rot: -9, w: 252, h: 316, scale: 0.92, opacity: 0},
  {x: 27, y: 60, rot: -12, w: 252, h: 316, scale: 0.88, opacity: 0},
];
const AUTHORED_EXIT: Slot = {x: 157, y: 290, rot: -22, w: 285, h: 358, scale: 0.9, opacity: 0};

/**
 * Re-express a slot on the shared card box, keeping the rendered size and the
 * on-screen position identical: the scale absorbs the smaller box, and x/y are
 * shifted to compensate for scaling about a different origin.
 */
function onSharedBox(slot: Slot): Slot {
  const scale = slot.scale * (slot.w / CARD_W);
  const visualX = slot.x + slot.w * ORIGIN_X * (1 - slot.scale);
  const visualY = slot.y + slot.h * ORIGIN_Y * (1 - slot.scale);
  return {
    ...slot,
    w: CARD_W,
    h: CARD_H,
    scale,
    x: visualX - CARD_W * ORIGIN_X * (1 - scale),
    y: visualY - CARD_H * ORIGIN_Y * (1 - scale),
  };
}

const SLOTS: Slot[] = AUTHORED.map(onSharedBox);
const EXIT: Slot = onSharedBox(AUTHORED_EXIT);
const HOVER_DELTA = [
  {dx: 0, dy: -6, drot: 0, dscale: 0},
  {dx: 34, dy: -22, drot: 5, dscale: 0.01},
  {dx: -36, dy: -12, drot: -5, dscale: 0.01},
  {dx: -10, dy: -8, drot: -8, dscale: 0},
  {dx: 10, dy: -8, drot: -8, dscale: 0},
];

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EXIT_EASE = 'cubic-bezier(0.55, 0, 0.45, 1)';
const SHIFT_MS = 720;
const EXIT_MS = 480;
const HOVER_MS = 520;

/** Natural footprint of the fan; it scales down to fit narrower columns. */
const FAN_W = 352;
const FAN_H = 470;
const FAN_PAD = 24;
const MAX_CARDS = 3;

const order = ref<StackEntry[]>(props.entries.slice(0, MAX_CARDS));
const flicking = ref(false);
const hovered = ref(false);
const enterId = ref<string | null>(null);
const scale = ref(1);
const spread = ref(1);
const wrapRef = ref<HTMLElement | null>(null);

let prevFront: string | null = props.entries[0]?.id ?? null;
let prevIds = new Set(props.entries.map((e) => e.id));
let flickTimer: number | undefined;
let enterTimer: number | undefined;
let resizeObserver: ResizeObserver | undefined;
let spreadQuery: MediaQueryList | undefined;

function applySpread(): void {
  spread.value = spreadQuery?.matches ? 1.3 : 1;
}

function measure(): void {
  const width = wrapRef.value?.clientWidth;
  if (width) {
    scale.value = Math.max(0.4, Math.min(1, (width - FAN_PAD) / FAN_W));
  }
}

onMounted(() => {
  measure();
  if (typeof ResizeObserver !== 'undefined' && wrapRef.value) {
    resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(wrapRef.value);
  }
  // In the single-column row the stack sits in a much wider column, so the fan
  // opens ~30% further apart horizontally.
  spreadQuery = window.matchMedia('(min-width: 641px) and (max-width: 1100px)');
  applySpread();
  spreadQuery.addEventListener('change', applySpread);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  spreadQuery?.removeEventListener('change', applySpread);
  window.clearTimeout(flickTimer);
  window.clearTimeout(enterTimer);
});

watch(() => props.entries, (entries) => {
  // A flick in flight would reorder the list we are about to replace, pushing
  // a freshly saved entry to the back. Cancel it.
  window.clearTimeout(flickTimer);
  flicking.value = false;
  const front = entries[0]?.id ?? null;
  // "Added" means a genuinely new card at the front, so replacing the example
  // stack with the user's first real entry still plays the slide-in.
  const isAdd = Boolean(front) && front !== prevFront && !prevIds.has(front!);
  order.value = entries.slice(0, MAX_CARDS);
  prevFront = front;
  prevIds = new Set(entries.map((e) => e.id));
  if (isAdd && front) {
    enterId.value = front;
    window.clearTimeout(enterTimer);
    enterTimer = window.setTimeout(() => {
      if (enterId.value === front) {
        enterId.value = null;
      }
    }, 40);
  }
});

function slotAt(index: number): Slot {
  const slot = SLOTS[index];
  if (spread.value === 1) {
    return slot;
  }
  return {...slot, x: slot.x + (slot.x + slot.w / 2 - FAN_W / 2) * (spread.value - 1)};
}

function withHover(index: number): Slot {
  const base = slotAt(index);
  const delta = HOVER_DELTA[index] ?? HOVER_DELTA[HOVER_DELTA.length - 1];
  return {
    ...base,
    x: base.x + delta.dx,
    y: base.y + delta.dy,
    rot: base.rot + delta.drot,
    scale: base.scale + delta.dscale,
  };
}

function transformOf(slot: Slot): string {
  return `translate(${slot.x}px, ${slot.y}px) rotate(${slot.rot}deg) scale(${slot.scale})`;
}

function rotateStack(): void {
  // With a single card there is nothing to promote — it would just fly out and
  // boomerang back.
  if (flicking.value || order.value.length <= 1) {
    return;
  }
  flicking.value = true;
  flickTimer = window.setTimeout(() => {
    order.value = order.value.length > 1
      ? [...order.value.slice(1), order.value[0]]
      : order.value;
    flicking.value = false;
  }, EXIT_MS);
}

function styleFor(index: number): Record<string, string> {
  if (enterId.value && order.value[index]?.id === enterId.value) {
    return {
      transform: 'translate(-70px, 150px) rotate(-18deg) scale(0.82)',
      opacity: '0',
      zIndex: String(SLOTS.length + 2),
      transition: 'none',
    };
  }

  if (flicking.value && index === 0) {
    return {
      transform: transformOf(EXIT),
      opacity: '0',
      zIndex: String(SLOTS.length + 2),
      transition: `transform ${EXIT_MS}ms ${EXIT_EASE}, opacity ${EXIT_MS}ms ${EXIT_EASE}`,
    };
  }

  const slotIndex = flicking.value ? index - 1 : index;
  const clamped = Math.max(0, Math.min(slotIndex, SLOTS.length - 1));
  const base = slotAt(clamped);
  const slot = hovered.value && base.opacity > 0 ? withHover(clamped) : base;
  const duration = hovered.value ? HOVER_MS : SHIFT_MS;

  return {
    transform: transformOf(slot),
    opacity: String(base.opacity),
    zIndex: String(SLOTS.length - slotIndex),
    transition: `transform ${duration}ms ${EASE}, opacity 420ms ease`,
  };
}

const visible = computed(() => order.value.slice(0, SLOTS.length));
/** Anything in motion — the only time the cards deserve a compositor layer. */
const isAnimating = computed(() => flicking.value || hovered.value || enterId.value !== null);

function onCardClick(index: number): void {
  if (index === 0 && !flicking.value) {
    rotateStack();
  }
}
</script>

<template>
  <div
    ref="wrapRef"
    class="jcards"
    :style="{height: `${FAN_H * scale}px`}"
    role="group"
    :aria-label="$t('journal_tile_stack_aria')"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <div class="jc-fan" :style="{transform: `scale(${scale})`}">
      <article
        v-for="(entry, index) in visible"
        :key="entry.id"
        class="jcard-slot"
        :class="{'is-front': index === 0 && !flicking, 'is-animating': isAnimating}"
        :aria-hidden="index !== 0"
        :style="styleFor(index)"
        @click="onCardClick(index)"
      >
        <journal-mood-card :mood="entry.mood" :text="entry.text" :date="entry.date"/>
      </article>
    </div>

    <!-- A real control for the flick, so the cards themselves stay readable
         content instead of being swallowed by one button's accessible name. -->
    <button
      v-if="visible.length > 1"
      type="button"
      class="jcards-next"
      @click="rotateStack"
    >
      {{ $t('journal_tile_next_entry') }}
    </button>
  </div>
</template>

<style scoped lang="scss">
// The design has no visible control for advancing the stack — a pointer just
// clicks a card. Keyboard users still need one, so the button is there but
// clipped out of sight, and only shows itself once focused.
.jcards-next {
  position: absolute;
  bottom: 0;
  left: 50%;
  z-index: 10;
  overflow: hidden;
  clip-path: inset(50%);
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  white-space: nowrap;
  background: transparent;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  color: var(--today-ink-on-pastel-muted);
  border-radius: 100px;

  &:focus-visible {
    overflow: visible;
    clip-path: none;
    width: auto;
    height: auto;
    padding: 6px 12px;
    transform: translateX(-50%);
  }

  @include focus-visible;
}


.jcards {
  position: relative;
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @include focus-visible;
}

.jc-fan {
  position: relative;
  width: 352px;
  height: 470px;
  flex: 0 0 auto;
  transform-origin: top center;
  will-change: transform;
}

.jcard-slot {
  position: absolute;
  left: 0;
  top: 0;
  width: 285px;
  height: 358px;
  transform-origin: 50% 60%;

  // Promoted only while the stack is actually animating — a permanent hint
  // would hold a compositor layer per card for the life of the page.
  &.is-animating {
    will-change: transform;
  }

  &.is-front {
    cursor: pointer;
  }
}

@media (prefers-reduced-motion: reduce) {
  .jcard-slot {
    transition: none !important;
  }
}
</style>
